import { ConnectError } from '@bufbuild/connect';
import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SHORT_DATE_FORMAT } from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';
import { Button, errorToastCaller, Spinner, toastCaller } from '@/shared/ui';

import { authModel, StaffRef, useStaffSecurityStatus } from '@/entities/auth';
import { ModalID, modalModel } from '@/entities/modal';

import styles from './StaffMFAPanel.module.scss';

type StaffMFAPanelProps = {
  staff: StaffRef;
  className?: string;
};

export const StaffMFAPanel: FC<StaffMFAPanelProps> = (props) => {
  const { className, staff } = props;

  const { formatDate, formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const [isRemoving, setIsRemoving] = useState(false);

  const { request, status, setStatus, loadingState, isDenied, fetchStatus } =
    useStaffSecurityStatus(staff, (statusRequest) =>
      dispatch(authModel.thunks.staffMFAStatus(statusRequest)).unwrap(),
    );

  const handleRemove = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.Confirm,
        data: {
          title: formatMessage({
            id: 'staffMFA.removeConfirmTitle',
            defaultMessage: 'Turn off two-step verification?',
          }),
          description: formatMessage({
            id: 'staffMFA.removeConfirmDescription',
            defaultMessage:
              'The authenticator app and the recovery codes of the staff member stop working. Where two-step verification is required, they must set it up again before they continue. The staff member will get an email about it.',
          }),
          okText: formatMessage({
            id: 'staffMFA.remove',
            defaultMessage: 'Turn off',
          }),
          danger: true,
          onConfirm: async () => {
            dispatch(modalModel.actions.closeModal(ModalID.Confirm));
            setIsRemoving(true);

            try {
              const response = await dispatch(
                authModel.thunks.staffMFARemove(request),
              ).unwrap();

              setStatus(response);
              toastCaller({
                type: 'success',
                message: formatMessage({
                  id: 'staffMFA.removeSuccess',
                  defaultMessage: 'Two-step verification turned off',
                }),
              });
            } catch (error) {
              errorToastCaller(error as ConnectError);
              await fetchStatus();
            } finally {
              setIsRemoving(false);
            }
          },
        },
      }),
    );
  };

  const renderBody = () => {
    if (loadingState === 'failed') {
      return (
        <>
          <p className={cn(styles.hint, 'p3t')}>
            {isDenied ? (
              <FormattedMessage
                id="staffMFA.accessDenied"
                defaultMessage="You cannot manage two-step verification of this staff member."
              />
            ) : (
              <FormattedMessage
                id="staffMFA.loadFailed"
                defaultMessage="The two-step verification status could not be loaded."
              />
            )}
          </p>

          {!isDenied && (
            <Button
              className={styles.retry}
              variant="tertiary"
              size="medium"
              onClick={fetchStatus}
            >
              <FormattedMessage id="global.retry" defaultMessage="Try again" />
            </Button>
          )}
        </>
      );
    }

    if (!status) {
      return <Spinner className={styles.spinner} />;
    }

    if (!status.Enabled) {
      return (
        <p className="p2">
          <FormattedMessage
            id="staffMFA.off"
            defaultMessage="Two-step verification is off."
          />
        </p>
      );
    }

    return (
      <div className={styles.status}>
        <p className="p2">
          {status.SetupAt && (
            <FormattedMessage
              id="staffMFA.onSince"
              defaultMessage="Two-step verification has been on since {date}"
              values={{
                date: formatDate(status.SetupAt.toDate(), SHORT_DATE_FORMAT),
              }}
            />
          )}
        </p>

        <Button
          className={styles.action}
          variant="tertiary"
          size="medium"
          danger
          loading={isRemoving}
          onClick={handleRemove}
        >
          <FormattedMessage id="staffMFA.remove" defaultMessage="Turn off" />
        </Button>
      </div>
    );
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-component="staff-mfa-panel"
    >
      <p className={cn(styles.hint, 'p3t')}>
        <FormattedMessage
          id="staffMFA.hint"
          defaultMessage="Turn it off if the staff member has lost their authenticator app and cannot sign in. The app and the recovery codes stop working, and they set up a new one. This does not change the two-step verification requirement in the staff table."
        />
      </p>

      {renderBody()}
    </div>
  );
};
